'use client';

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useId,
    useMemo,
    useRef,
    useSyncExternalStore,
    type ReactNode,
} from 'react';

import type {
    EventPriceHistoryPayload,
    GraphHeaderPayloadBase,
    GraphPayloadType,
    MarketPriceHistoryPayload,
    NumericForecastPayload,
} from 'components/Graph/headers/payloads';

/**
 * Cross-cutting header inputs. These come from the parent of the graph subtree
 * (PrimaryContent, SlideChartWidget, ChartWidget types, etc.) and are shared
 * by every header type.
 *
 * Type-specific extras (e.g. `onYoutubeClick` for `ExternalFinancialHeader`)
 * are passed directly to the specific header component, not through this
 * shared surface.
 */
export interface GraphHeaderInputs {
    /** Mirrors `Event.seriesTicker` — feeds `eventToFinancialName`. */
    eventSeriesTicker?: string;
    /** Mirrors `Event.rawMarkets[0]?.subtitle` — knockout decimal-precision recovery text. */
    eventTargetSubtitle?: string;
    /** Mirrors `Event.expirationValue`. */
    eventExpirationValue?: string;
    totalReturnInDollars?: number;
    onPositionClick?: () => void;
    /** When set, the shell renders a CountdownTimer next to the projection. */
    highlightedCloseDate?: Date;
    /**
     * When true, the shell suppresses every chart-bound element (projection,
     * suffix, subtext, maturity warning, rightSlot, highlighted-close-date
     * countdown). The ROI pill still renders when `totalReturnInDollars` is
     * defined — ROI reflects the user's resting P&L, not chart state, so it
     * survives any chart-area takeover.
     */
    chartHidden?: boolean;
}

/**
 * Per-publisher payload store. Each publisher (see `usePublishGraphPayload`)
 * owns exactly one current payload — there is no separate slot bookkeeping.
 * Slot lookup (`get`) is derived by filtering publishers on `payload.type`,
 * so a publisher whose payload type changes between renders can't leave a
 * stale entry behind in its previous slot.
 *
 * In normal use every dual-mount publishes a different `payload.type`, so
 * each slot has a single live publisher; the dev-only warning below catches
 * future regressions where two publishers collide on the same slot. `get`
 * returns the most recently registered publisher's payload (insertion order
 * is preserved by `Map`, and `set` deletes-then-inserts to bump the freshest
 * entry to the end); `clearPublisher` removes the publisher entirely.
 */
interface PayloadStore {
    get: (type: GraphPayloadType) => GraphHeaderPayloadBase | undefined;
    set: (publisherId: string, payload: GraphHeaderPayloadBase) => void;
    clearPublisher: (publisherId: string) => void;
    subscribe: (type: GraphPayloadType, fn: () => void) => () => void;
}

/**
 * Shallow-equal across all enumerable payload fields. Payloads are
 * primitive-only by contract (functions and arrays must come from a memoized
 * source so their references stay stable across renders), which lets this
 * short-circuit re-publishes for every chart type.
 */
const shallowEqualPayload = (a: GraphHeaderPayloadBase, b: GraphHeaderPayloadBase): boolean => {
    if (a === b) return true;
    const aRecord = a as unknown as Record<string, unknown>;
    const bRecord = b as unknown as Record<string, unknown>;
    const aKeys = Object.keys(aRecord);
    const bKeys = Object.keys(bRecord);
    if (aKeys.length !== bKeys.length) return false;
    for (const key of aKeys) {
        if (!Object.is(aRecord[key], bRecord[key])) return false;
    }
    return true;
};

const createPayloadStore = (): PayloadStore => {
    const publishers = new Map<string, GraphHeaderPayloadBase>();
    const listeners = new Map<GraphPayloadType, Set<() => void>>();

    const notify = (type: GraphPayloadType): void => {
        listeners.get(type)?.forEach((l) => l());
    };

    return {
        get: (type) => {
            // Most recently registered publisher of this type wins. Map
            // iteration order matches insertion order, and `set` always
            // deletes-then-inserts so the freshest entry sorts to the end.
            let latest: GraphHeaderPayloadBase | undefined;
            for (const payload of publishers.values()) {
                if (payload.type === type) latest = payload;
            }
            return latest;
        },
        set: (publisherId, payload) => {
            const previous = publishers.get(publisherId);
            if (previous && shallowEqualPayload(previous, payload)) return;

            // Delete-then-set so the publisher always lands at the end of
            // insertion order, which keeps `get`'s "most recently
            // registered" semantics correct when the same publisher updates.
            publishers.delete(publisherId);
            publishers.set(publisherId, payload);

            // If the publisher's slot changed, notify the old slot too so
            // its subscribers recompute and drop the now-orphaned entry.
            if (previous && previous.type !== payload.type) {
                notify(previous.type);
            }
            notify(payload.type);

            if (process.env.NODE_ENV !== 'production') {
                const ids: string[] = [];
                for (const [id, p] of publishers) {
                    if (p.type === payload.type) ids.push(id);
                }
                if (ids.length > 1) {
                    console.warn(
                        `[GraphInteractionProvider] Multiple publishers in slot "${payload.type}" (${ids.join(', ')}). Only the most recently registered will be visible to headers — this almost certainly indicates a missing dual-mount discriminator.`,
                    );
                }
            }
        },
        clearPublisher: (publisherId) => {
            const previous = publishers.get(publisherId);
            if (!previous) return;
            publishers.delete(publisherId);
            notify(previous.type);
        },
        subscribe: (type, fn) => {
            let typeListeners = listeners.get(type);
            if (!typeListeners) {
                typeListeners = new Set();
                listeners.set(type, typeListeners);
            }
            typeListeners.add(fn);
            return () => {
                typeListeners.delete(fn);
            };
        },
    };
};

interface GraphInteractionContextValue {
    headerInputs: GraphHeaderInputs;
    payloadStore: PayloadStore;
}

// The default context is intentionally backed by a no-op store. Any publisher
// rendered outside a `GraphInteractionProvider` (e.g. a bare graph mounted
// inside a widget route that never renders a sibling header consumer) would
// otherwise share a single module-level store with every other unprovided
// subtree, cross-pollinating their published payloads. The no-op store keeps
// no state, never notifies, and returns safe defaults — so the publisher
// hooks remain valid no-ops and `useSyncExternalStore`'s stable-identity
// contract still holds via this module-level singleton.
const NOOP_UNSUBSCRIBE = (): void => {};

const NOOP_PAYLOAD_STORE: PayloadStore = {
    get: () => undefined,
    set: () => {},
    clearPublisher: () => {},
    subscribe: () => NOOP_UNSUBSCRIBE,
};

const DEFAULT_CONTEXT: GraphInteractionContextValue = {
    headerInputs: {},
    payloadStore: NOOP_PAYLOAD_STORE,
};

const GraphInteractionContext = createContext<GraphInteractionContextValue>(DEFAULT_CONTEXT);

interface GraphInteractionProviderProps extends GraphHeaderInputs {
    children: ReactNode;
}

/**
 * Wrap a graph subtree so that its body can publish header state and a
 * sibling header component can read it. Header inputs are shared via the same
 * provider so the parent has a single place to wire up event metadata,
 * totalReturn, and highlighted close date.
 */
export const GraphInteractionProvider = ({
    children,
    eventSeriesTicker,
    eventTargetSubtitle,
    eventExpirationValue,
    totalReturnInDollars,
    onPositionClick,
    highlightedCloseDate,
    chartHidden,
}: GraphInteractionProviderProps) => {
    const payloadStoreRef = useRef<PayloadStore | null>(null);
    if (payloadStoreRef.current === null) {
        payloadStoreRef.current = createPayloadStore();
    }

    const headerInputs = useMemo<GraphHeaderInputs>(
        () => ({
            eventSeriesTicker,
            eventTargetSubtitle,
            eventExpirationValue,
            totalReturnInDollars,
            onPositionClick,
            highlightedCloseDate,
            chartHidden,
        }),
        [
            eventSeriesTicker,
            eventTargetSubtitle,
            eventExpirationValue,
            totalReturnInDollars,
            onPositionClick,
            highlightedCloseDate,
            chartHidden,
        ],
    );

    const value = useMemo<GraphInteractionContextValue>(
        () => ({
            headerInputs,
            payloadStore: payloadStoreRef.current!,
        }),
        [headerInputs],
    );

    return <GraphInteractionContext.Provider value={value}>{children}</GraphInteractionContext.Provider>;
};

export const useGraphHeaderInputs = (): GraphHeaderInputs => useContext(GraphInteractionContext).headerInputs;

/**
 * Read the current per-graph payload for a specific discriminator. Each
 * header subscribes to the slot for its own `type` so unrelated bodies (e.g.
 * a dual-mounted price-history fallback sharing the provider with an
 * artist-vs streams body) can't trample its payload — the two bodies write
 * to independent slots and only the matching header re-renders.
 */
export const useGraphPayload = <T extends GraphHeaderPayloadBase>(type: T['type']): T | undefined => {
    const { payloadStore } = useContext(GraphInteractionContext);
    const subscribe = useCallback((fn: () => void) => payloadStore.subscribe(type, fn), [payloadStore, type]);
    const getSnapshot = useCallback(() => payloadStore.get(type) as T | undefined, [payloadStore, type]);
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
};

/**
 * True when whichever chart body is publishing here has signalled it is in
 * its empty (poor-graph / uiHidden) branch. Sibling chrome that lives next
 * to the chart but outside any header (e.g. the Kalshi logo in
 * `EventChartToggleRow`) reads this so it can disappear together with the
 * chart and its header rather than floating above empty space.
 *
 * Only chart types that have a poor-graph empty branch participate today:
 * `eventPriceHistory`, `marketPriceHistory`, and `numericForecast`. Other
 * payload slots (artistVs, weather, knockout, ...) don't publish from an
 * empty branch, so they're skipped here.
 */
export const useChartUnavailable = (): boolean => {
    const eventPriceHistory = useGraphPayload<EventPriceHistoryPayload>('eventPriceHistory');
    const marketPriceHistory = useGraphPayload<MarketPriceHistoryPayload>('marketPriceHistory');
    const numericForecast = useGraphPayload<NumericForecastPayload>('numericForecast');
    return Boolean(
        eventPriceHistory?.chartUnavailable ||
        marketPriceHistory?.chartUnavailable ||
        numericForecast?.chartUnavailable,
    );
};

/**
 * Publish a typed primitive-only payload from the graph body. Headers
 * subscribe to the matching slot and compose their own JSX from the data
 * fields.
 *
 * Callers must memoize `payload` (typically with `useMemo`) — the effect's
 * single dep is the payload reference, so an unstable identity will re-run
 * the effect every render. The store still dedupes via `shallowEqualPayload`,
 * but stable references keep the publish path cheap.
 *
 * Each call site gets its own `useId()` so multiple bodies sharing a provider
 * (e.g. dual-mounted artist-vs streams body + price-history fallback) write
 * to independent entries within their respective slots. The publisher's
 * entry is removed on unmount so the slot doesn't return stale data.
 */
export const usePublishGraphPayload = <T extends GraphHeaderPayloadBase>(payload: T): void => {
    const { payloadStore } = useContext(GraphInteractionContext);
    const publisherId = useId();

    useEffect(() => {
        payloadStore.set(publisherId, payload);
    }, [payloadStore, publisherId, payload]);

    useEffect(
        () => () => {
            payloadStore.clearPublisher(publisherId);
        },
        [payloadStore, publisherId],
    );
};
